import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import translate from '@translate';
import Table from 'src/components/Table';
import { ReceivingIcon } from 'src/components/SkladonIcon/Icons';
import {
    GridFilterType,
    GridSortType,
    ProductDetailGridTableRefType,
    RowActionPropsType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType,
} from 'src/types/table';
import { ProductReceivingsType } from 'src/types/productDetail';
import { UserSettingsType } from 'src/types/userSettings';
import { ConstantsCodeType, ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { colors } from 'src/themes/default';
import getStatusColor from 'src/utils/getStatusColor';
import colorOptionFce from 'src/utils/colorOptionFce';

const StyledContainer = styled('div')({
    padding: '16px 0',
});

const OPTIONS_SESSION_NAME = 'productReceivingsTable';

const allColumns: TableColumnType[] = [
    { label: 'asn', key: 'ASN', type: 'string', defaultShow: true },
    { label: 'eshopReceivingId', key: 'ESHOP_RECEIVING_ID', type: 'string', defaultShow: true },
    { label: 'createdAt', key: 'CREATED_AT', type: 'dateTime', defaultShow: true },
    {
        label: 'productQuantity',
        key: 'PRODUCT_QUANTITY',
        type: 'number-filter',
        customRender: true,
        defaultShow: true,
    },
    { label: 'productReceived', key: 'PRODUCT_RECEIVED', type: 'number-filter', defaultShow: true },
    { label: 'productDiffQuantity', key: 'PRODUCT_DIFF_QUANTITY', type: 'number-filter', defaultShow: true },
    {
        label: 'status',
        key: 'STATUS',
        type: 'select',
        selectOption: 'receivingStatusCodes',
        customRender: true,
        optionStyleFce: true,
        defaultShow: true,
    },
    { label: 'estimatedDelivery', key: 'ESTIMATED_DELIVERY', type: 'dateTime', defaultShow: true },
    { label: 'unloadedAt', key: 'UNLOADED_AT', type: 'dateTime', defaultShow: true },
    { label: 'receivedAt', key: 'RECEIVED_AT', type: 'dateTime', defaultShow: false },
    { label: 'confirmedAt', key: 'CONFIRMED_AT', type: 'dateTime', defaultShow: true },
];

const DEFAULT_ITEMS = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

/** Sloupce nutné pro logiku tabulky (rowIdKey, odkaz na příjem, šedá Avizováno ks), nejsou v hlavičce. */
const HIDDEN_GRID_COLUMNS = ['ID', 'RECEIVING_ID', 'LINE_STATE'] as const;

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'CREATED_AT', direction: 'DESC' },
    page: 1,
    items: DEFAULT_ITEMS,
    columns: [...allColumns],
};

function getInitialTableOptions(): TablePropsOptionsType {
    try {
        const session = JSON.parse(sessionStorage.getItem(OPTIONS_SESSION_NAME) || 'null');
        if (session && typeof session.items === 'number' && ROWS_PER_PAGE_OPTIONS.includes(session.items)) {
            return { ...initTableOptions, ...session, columns: session.columns || initTableOptions.columns };
        }
    } catch {
        // ignore invalid session
    }
    return initTableOptions;
}

export function buildProductReceivingsGridPayload(
    options: TablePropsOptionsType,
    idProduct: string
): {
    page: number;
    items: number;
    sorts: GridSortType[];
    filters: GridFilterType[][];
    columns: string[];
} {
    const items = ROWS_PER_PAGE_OPTIONS.includes(options.items) ? options.items : DEFAULT_ITEMS;
    const userConditions = (options.filters || []).flat().filter((c) => c && c.column);
    const productFilter = { column: 'PRODUCT_ID', operator: 'eq' as const, value: idProduct };
    return {
        page: options.page,
        items,
        sorts: [options.sort],
        filters: [[...userConditions, productFilter]],
        columns: [...allColumns.map((c) => c.key), ...HIDDEN_GRID_COLUMNS],
    };
}

export interface ProductReceivingsTablePropsType {
    data: ProductReceivingsType | null;
    loading?: boolean;
    onFetchData: (payload: ReturnType<typeof buildProductReceivingsGridPayload>) => void;
    idProduct: string;
    receivingStatusCodes: ConstantsCodeType[];
    receivingStatusCodesObject: ConstantsCodesObjectType;
    permissionReceivingDetail: boolean;
    userSettingsData: UserSettingsType | null;
    editUserSettings: (newUserSettings: UserSettingsType) => void;
}

const ProductReceivingsTable = forwardRef<ProductDetailGridTableRefType, ProductReceivingsTablePropsType>(
    (props, ref) => {
        const tableRef = useRef<TableRefType>(null);
        const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(getInitialTableOptions);

        const rows: TableRowType[] = props.data?.data ?? [];
        const totalCount = props.data?.totalCount ?? 0;

        const loadTableData = useCallback(() => {
            if (props.idProduct && tableOptions.columns.length) {
                props.onFetchData(buildProductReceivingsGridPayload(tableOptions, props.idProduct));
            }
        }, [props.idProduct, props.onFetchData, tableOptions]);

        useImperativeHandle(
            ref,
            (): ProductDetailGridTableRefType => ({
                exportTable: (type, filename, getPrintRows, extendedColmns) =>
                    tableRef.current?.exportTable(type, filename, getPrintRows, extendedColmns),
                exportTableGrid: (type, filename, getGridParams, requestUrl, extendedColmns, extendOptions) =>
                    tableRef.current?.exportTableGrid(
                        type,
                        filename,
                        getGridParams,
                        requestUrl,
                        extendedColmns,
                        extendOptions
                    ),
                getGridParams: () => buildProductReceivingsGridPayload(tableOptions, props.idProduct),
            }),
            [tableOptions, props.idProduct]
        );

        useEffect(() => {
            loadTableData();
        }, [tableOptions]);

        const handleChangeOptions = useCallback((newOptions: TablePropsOptionsType) => {
            setTableOptions(newOptions);
        }, []);

        const customRender = useCallback(
            (column: TableColumnType, row: TableRowType) => {
                const value = row[column.key];
                switch (column.key) {
                    case 'CREATED_AT':
                    case 'ESTIMATED_DELIVERY':
                    case 'UNLOADED_AT':
                    case 'RECEIVED_AT':
                    case 'CONFIRMED_AT':
                        return value ? moment(value as string).format('DD.MM.YYYY HH:mm') : '';
                    case 'STATUS':
                        return (
                            <span style={{ color: getStatusColor(props.receivingStatusCodesObject[row.STATUS]?.color) }}>
                                {props.receivingStatusCodesObject[row.STATUS]?.label}
                            </span>
                        );
                    case 'PRODUCT_QUANTITY':
                        if (String(row.LINE_STATE ?? '').toUpperCase() === 'D') {
                            return <span style={{ color: colors.text_T3 }}>{value}</span>;
                        }
                        return value;
                    default:
                        return value;
                }
            },
            [props.receivingStatusCodesObject]
        );

        const tableRowActions: RowActionPropsType[] = [
            {
                icon: <ReceivingIcon />,
                label: translate('receivings.action.receivingDetail'),
                getLink: (row) => `/receivings/${row.RECEIVING_ID}/`,
                hiddenFce: () => !props.permissionReceivingDetail,
            },
        ];

        const tableSelectOptions: {
            [key: string]: ConstantsCodeType[];
        } = {
            receivingStatusCodes: props.receivingStatusCodes,
        };

        return (
            <StyledContainer>
                <Table
                    ref={tableRef}
                    rowIdKey="ID"
                    rows={rows}
                    allColumns={allColumns}
                    columsLabelPrefix="productDetail.productReceivingsTable"
                    options={tableOptions}
                    totalCount={totalCount}
                    loading={props.loading}
                    customRender={customRender}
                    heightVariant="top-tab"
                    fullHeight
                    optionsSessionName={OPTIONS_SESSION_NAME}
                    selectOptions={tableSelectOptions}
                    onChangeOptions={handleChangeOptions}
                    showClearFilters
                    rowActions={tableRowActions}
                    optionStyleFce={colorOptionFce}
                    userSettingsName="showProductDetailReceivingsTableColumns"
                    userSettingsData={props.userSettingsData}
                    editUserSettings={props.editUserSettings}
                />
            </StyledContainer>
        );
    }
);

ProductReceivingsTable.displayName = 'ProductReceivingsTable';

export default ProductReceivingsTable;
